import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, Mail, MessageSquare,
  LogOut, RefreshCw, DollarSign, FileText, TrendingUp, ExternalLink,
  Send, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { adminStats, adminList } from "@/lib/api";
import { SITE } from "@/lib/site";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import ReplyDialog from "@/components/site/ReplyDialog";

const TABS = [
  { key: "enrollments", label: "Enrollments", icon: GraduationCap },
  { key: "store-leads", label: "Store Leads", icon: FileText },
  { key: "contacts", label: "Contacts", icon: MessageSquare },
  { key: "resource-leads", label: "Resource Leads", icon: FileText },
  { key: "newsletter", label: "Newsletter", icon: Mail },
];

// Static column headers — extracted outside component to keep stable identity across renders
const ENROLLMENT_HEADERS = ["Course", "Amount", "Student", "Email", "Status", "PayPal Order", "Date"];
const CONTACT_HEADERS = ["Name", "Email · Phone", "Organization", "Interest", "Message", "Date", "Action"];
const RESOURCE_LEAD_HEADERS = ["Name", "Email", "Resource Type", "Resource", "Date"];
const NEWSLETTER_HEADERS = ["Email", "Subscribed"];
const STORE_LEAD_HEADERS = ["Name", "Email", "Phone", "Product", "Price", "Date"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("enrollments");
  const [data, setData] = useState({ enrollments: null, contacts: null, "resource-leads": null, newsletter: null, "store-leads": null });
  const [loading, setLoading] = useState(false);
  const [replyContact, setReplyContact] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const s = await adminStats();
      setStats(s);
    } catch {
      toast.error("Failed to load stats");
    }
  }, []);

  const loadList = useCallback(async (kind) => {
    setLoading(true);
    try {
      const d = await adminList(kind, { limit: 100 });
      setData((prev) => ({ ...prev, [kind]: d }));
    } catch {
      toast.error(`Failed to load ${kind}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadStats(), loadList(tab)]);
    toast.success("Dashboard refreshed");
  }, [loadStats, loadList, tab]);

  // Initial load
  useEffect(() => {
    loadStats();
    loadList("enrollments");
  }, [loadStats, loadList]);

  // Lazy-load each tab the first time it's viewed
  useEffect(() => {
    if (!data[tab]) loadList(tab);
  }, [tab, data, loadList]);

  const onLogout = async () => {
    await logout();
    toast.message("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--trj-light)]" data-testid="admin-dashboard-page">
      {/* TOP BAR */}
      <header className="bg-[var(--trj-navy)] text-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
              <img src={SITE.assets.logo} alt="TRJ Services" className="h-9 bg-white p-1.5" />
            </Link>
            <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-widest text-[var(--trj-gold)]">
              <LayoutDashboard className="w-3.5 h-3.5" /> Admin Dashboard
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-white/70">
              <Users className="w-3.5 h-3.5 text-[var(--trj-gold)]" /> {user?.email}
            </div>
            <button
              onClick={refreshAll}
              className="text-xs text-white/80 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/30"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Link
              to="/"
              target="_blank"
              className="hidden sm:inline-flex text-xs text-white/80 hover:text-white items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/30"
              data-testid="admin-view-site-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <button
              onClick={onLogout}
              className="text-xs text-white/80 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-white/30"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {/* HEADER */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="trj-eyebrow mb-2">Welcome back</div>
            <h1 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)]">{user?.name || "TRJ Admin"}</h1>
            <p className="text-sm text-[var(--trj-muted)] mt-1">Here&apos;s what&apos;s happening across TRJ Services today.</p>
          </div>
        </div>

        {/* STATS */}
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200" data-testid="admin-stats">
          <StatCard
            icon={DollarSign}
            label="Tax School Revenue"
            value={stats ? fmtMoney(stats.revenue_total) : "—"}
            accent="gold"
            testid="stat-revenue"
          />
          <StatCard
            icon={GraduationCap}
            label="Enrollments"
            value={stats?.enrollment_count ?? "—"}
            sub={stats ? `+${stats.recent_enrollments_7d} last 7d` : null}
            testid="stat-enrollments"
          />
          <StatCard
            icon={MessageSquare}
            label="Contact Inquiries"
            value={stats?.contact_count ?? "—"}
            sub={stats ? `+${stats.recent_contacts_7d} last 7d` : null}
            testid="stat-contacts"
          />
          <StatCard
            icon={FileText}
            label="Resource Leads"
            value={stats?.resource_lead_count ?? "—"}
            testid="stat-leads"
          />
          <StatCard
            icon={Mail}
            label="Newsletter Subs"
            value={stats?.newsletter_count ?? "—"}
            testid="stat-newsletter"
          />
        </section>

        {/* TABS */}
        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList
              data-testid="admin-tabs-list"
              className="bg-white p-1 rounded-none border border-gray-200 inline-flex h-auto flex-wrap"
            >
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={`admin-tab-${t.key}`}
                  className="rounded-none data-[state=active]:bg-[var(--trj-navy)] data-[state=active]:text-white px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="enrollments" className="mt-6">
              <EnrollmentsTable data={data.enrollments} loading={loading} />
            </TabsContent>
            <TabsContent value="contacts" className="mt-6">
              <ContactsTable
                data={data.contacts}
                loading={loading}
                onReply={setReplyContact}
              />
            </TabsContent>
            <TabsContent value="resource-leads" className="mt-6">
              <ResourceLeadsTable data={data["resource-leads"]} loading={loading} />
            </TabsContent>
            <TabsContent value="newsletter" className="mt-6">
              <NewsletterTable data={data.newsletter} loading={loading} />
            </TabsContent>
            <TabsContent value="store-leads" className="mt-6">
              <StoreLeadsTable data={data["store-leads"]} loading={loading} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <ReplyDialog
        open={!!replyContact}
        onOpenChange={(v) => { if (!v) setReplyContact(null); }}
        contact={replyContact}
        onSent={() => {
          // refresh contacts list to pick up last_replied_at
          loadList("contacts");
          loadStats();
        }}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="bg-white p-6 relative" data-testid={testid}>
      <div className={`w-10 h-10 flex items-center justify-center ${accent === "gold" ? "bg-[var(--trj-gold)] text-[var(--trj-navy)]" : "bg-[var(--trj-navy)] text-[var(--trj-gold)]"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)] mt-4">{label}</div>
      <div className={`font-serif-trj text-3xl mt-1 ${accent === "gold" ? "text-[var(--trj-gold)]" : "text-[var(--trj-navy)]"}`}>{value}</div>
      {sub && (
        <div className="text-[11px] mt-1 inline-flex items-center gap-1 text-[var(--trj-green)]">
          <TrendingUp className="w-3 h-3" /> {sub}
        </div>
      )}
    </div>
  );
}

function TableShell({ headers, rows, loading, emptyMessage, testid }) {
  return (
    <div className="bg-white border border-gray-200 overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--trj-light)] border-b border-gray-200">
              {headers.map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-[var(--trj-muted)] font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={headers.length} className="text-center py-12 text-[var(--trj-muted)]">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={headers.length} className="text-center py-12 text-[var(--trj-muted)]">{emptyMessage}</td>
              </tr>
            )}
            {!loading && rows}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EnrollmentsTable({ data, loading }) {
  const rows = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((e) => (
      <tr key={e.id} className="border-b border-gray-100 hover:bg-[var(--trj-light)]/60" data-testid={`row-enrollment-${e.id}`}>
        <td className="px-4 py-3 font-medium text-[var(--trj-navy)]">{e.course_title}</td>
        <td className="px-4 py-3 text-[var(--trj-gold)] font-semibold">{fmtMoney(e.amount)}</td>
        <td className="px-4 py-3">{e.student_name || e.paypal_payer_name || "—"}</td>
        <td className="px-4 py-3 text-[var(--trj-muted)]">{e.student_email || e.paypal_payer_email || "—"}</td>
        <td className="px-4 py-3">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${e.status === "COMPLETED" ? "bg-[var(--trj-green)]/10 text-[var(--trj-green)]" : "bg-[var(--trj-light)] text-[var(--trj-muted)]"}`}>
            {e.status}
          </span>
        </td>
        <td className="px-4 py-3 font-mono text-[11px] text-[var(--trj-muted)]">{e.paypal_order_id}</td>
        <td className="px-4 py-3 text-[var(--trj-muted)]">{fmtDate(e.created_at)}</td>
      </tr>
    ));
  }, [data]);

  return (
    <TableShell
      headers={ENROLLMENT_HEADERS}
      rows={rows}
      loading={loading}
      emptyMessage="No enrollments yet. Once a student completes PayPal checkout, they'll appear here."
      testid="admin-enrollments-table"
    />
  );
}

function ContactsTable({ data, loading, onReply }) {
  const rows = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((c) => (
      <tr key={c.id} className="border-b border-gray-100 hover:bg-[var(--trj-light)]/60 align-top" data-testid={`row-contact-${c.id}`}>
        <td className="px-4 py-3 font-medium text-[var(--trj-navy)] whitespace-nowrap">
          {c.name}
          {c.last_replied_at && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--trj-green)]">
              <CheckCircle2 className="w-3 h-3" /> Replied {fmtDate(c.last_replied_at)}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-[var(--trj-muted)] whitespace-nowrap">
          <a href={`mailto:${c.email}`} className="hover:text-[var(--trj-green)]">{c.email}</a>
          {c.phone && <div className="text-[11px] text-[var(--trj-muted)]">{c.phone}</div>}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">{c.organization || "—"}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--trj-light)] text-[var(--trj-navy)]">
            {c.service_interest || "General"}
          </span>
        </td>
        <td className="px-4 py-3 max-w-[420px]">
          <div className="text-[13px] text-[var(--trj-navy)] line-clamp-3">{c.message}</div>
        </td>
        <td className="px-4 py-3 text-[var(--trj-muted)] whitespace-nowrap">{fmtDate(c.created_at)}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <button
            onClick={() => onReply && onReply(c)}
            className="trj-btn-green text-[11px] px-3 py-1.5"
            data-testid={`btn-reply-${c.id}`}
          >
            <Send className="w-3.5 h-3.5" /> Reply
          </button>
        </td>
      </tr>
    ));
  }, [data, onReply]);

  return (
    <TableShell
      headers={CONTACT_HEADERS}
      rows={rows}
      loading={loading}
      emptyMessage="No contact submissions yet."
      testid="admin-contacts-table"
    />
  );
}

function ResourceLeadsTable({ data, loading }) {
  const rows = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((l) => (
      <tr key={l.id} className="border-b border-gray-100 hover:bg-[var(--trj-light)]/60" data-testid={`row-lead-${l.id}`}>
        <td className="px-4 py-3 font-medium text-[var(--trj-navy)]">{l.name || "—"}</td>
        <td className="px-4 py-3">
          <a href={`mailto:${l.email}`} className="text-[var(--trj-muted)] hover:text-[var(--trj-green)]">{l.email}</a>
        </td>
        <td className="px-4 py-3">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--trj-light)] text-[var(--trj-navy)]">
            {l.resource_type}
          </span>
        </td>
        <td className="px-4 py-3 text-[var(--trj-navy)]">{l.resource_title}</td>
        <td className="px-4 py-3 text-[var(--trj-muted)] whitespace-nowrap">{fmtDate(l.created_at)}</td>
      </tr>
    ));
  }, [data]);

  return (
    <TableShell
      headers={RESOURCE_LEAD_HEADERS}
      rows={rows}
      loading={loading}
      emptyMessage="No resource leads yet."
      testid="admin-leads-table"
    />
  );
}

function NewsletterTable({ data, loading }) {
  const rows = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((n) => (
      <tr key={n.id} className="border-b border-gray-100 hover:bg-[var(--trj-light)]/60" data-testid={`row-newsletter-${n.id}`}>
        <td className="px-4 py-3">
          <a href={`mailto:${n.email}`} className="text-[var(--trj-navy)] font-medium hover:text-[var(--trj-green)]">{n.email}</a>
        </td>
        <td className="px-4 py-3 text-[var(--trj-muted)] whitespace-nowrap">{fmtDate(n.created_at)}</td>
      </tr>
    ));
  }, [data]);

  return (
    <TableShell
      headers={NEWSLETTER_HEADERS}
      rows={rows}
      loading={loading}
      emptyMessage="No newsletter subscribers yet."
      testid="admin-newsletter-table"
    />
  );
}

function StoreLeadsTable({ data, loading }) {
  const rows = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((l) => (
      <tr key={l.id} className="border-b border-gray-100 hover:bg-[var(--trj-light)]/60" data-testid={`row-store-lead-${l.id}`}>
        <td className="px-4 py-3 font-medium text-[var(--trj-navy)]">{l.first_name} {l.last_name}</td>
        <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-[var(--trj-muted)] hover:text-[var(--trj-green)]">{l.email}</a></td>
        <td className="px-4 py-3 text-[var(--trj-muted)]">{l.phone || "—"}</td>
        <td className="px-4 py-3 text-[var(--trj-navy)]">{l.product_title}</td>
        <td className="px-4 py-3 text-[var(--trj-gold)] font-semibold">${l.price}</td>
        <td className="px-4 py-3 text-[var(--trj-muted)] whitespace-nowrap">{fmtDate(l.created_at)}</td>
      </tr>
    ));
  }, [data]);

  return (
    <TableShell
      headers={STORE_LEAD_HEADERS}
      rows={rows}
      loading={loading}
      emptyMessage="No store leads yet."
      testid="admin-store-leads-table"
    />
  );
}
